// src/shared/telemetry/MagnitudeCard.tsx
// ────────────────────────────────────────────────────────────────────────────
// MagnitudeCard — intercalated [SignalStatsTable → TelemetryTimeSeriesPlot]
// for one telemetry magnitude.
//
// Generic: works for single-source (DaemonOps) and multi-source (IoT fleet).
// Renders per-magnitude stats above the cardiogram plot.
//
// PAC Glass design system.
// ────────────────────────────────────────────────────────────────────────────

import React from 'react'
import TelemetryTimeSeriesPlot from '@/shared/components/plots/TelemetryTimeSeriesPlot'
import type { TimeSeriesPoint, PlotMarker } from '@/shared/components/plots/TelemetryTimeSeriesPlot'
import SignalStatsTable from './SignalStatsTable'
import type { MagnitudeDefinition, MetricGroup, PlotOverlay } from './types'
import { computeStats } from './telemetryUtils'

/* ── Props ──────────────────────────────────────────────────────────── */

export interface MagnitudeCardProps {
  /** Magnitude definition (key, title, unit, color, yFloor, yCeil). */
  magnitude: MagnitudeDefinition
  /** Primary data series — used for single-source mode (DaemonOps). */
  data?: TimeSeriesPoint[]
  /** Multi-source overlays — used for fleet mode (IoT). */
  overlays?: PlotOverlay[]
  /** Pre-built MetricGroup for grouped stats (IoT fleet).
   *  If provided, renders collapsible grouped table. */
  group?: MetricGroup
  /** Time window in seconds for the plot. */
  windowSec: number
  /** Cross-plot hover sync ref. */
  hoverTsRef: React.MutableRefObject<number | null>
  /** Plot height in CSS px (default 200, IoT uses 510). */
  plotHeight?: number
  /** Optional vertical markers for the plot (e.g. run-start events). */
  markers?: PlotMarker[]
}

/* ── Component ──────────────────────────────────────────────────────── */

export default function MagnitudeCard({
  magnitude,
  data,
  overlays,
  group,
  windowSec,
  hoverTsRef,
  plotHeight = 200,
  markers,
}: MagnitudeCardProps) {
  // Single-source stats (flat table) — only when no grouped data
  const hasFlatData = !!data && data.length > 0 && !group
  const hasGroupData = !!group

  return (
    <div className="flex flex-col gap-1">
      {/* ── Stats table (intercalated above plot) ──────────────── */}
      {hasGroupData && (
        <SignalStatsTable groups={[group]} />
      )}
      {hasFlatData && (
        <SignalStatsTable
          flatConfigs={[{
            key: magnitude.key,
            title: magnitude.title,
            unit: magnitude.unit,
            color: magnitude.color,
          }]}
          flatSeries={{ [magnitude.key]: data! }}
        />
      )}

      {/* ── Cardiogram plot ────────────────────────────────────── */}
      <TelemetryTimeSeriesPlot
        data={overlays ? [] : (data ?? [])}
        overlays={overlays}
        title={magnitude.title}
        unit={magnitude.unit}
        color={magnitude.color}
        minHeight={plotHeight}
        windowSec={windowSec}
        hoverTsRef={hoverTsRef}
        yFloor={magnitude.yFloor}
        yCeil={magnitude.yCeil}
        markers={markers}
      />
    </div>
  )
}
